"use client"

import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./card.jsx"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./table.jsx"
import { Input } from "./input.jsx"
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "./pagination.jsx"
import { Search } from "lucide-react"

interface ChartData {
    name: string
    value: number
    [key: string]: any
}

interface DataTableProps {
    data: ChartData[]
    width: number
    height: number
    title: string
    rowsPerPage: number
    enablePagination: boolean
    maxRows: number
    enableSearch: boolean
    enableDateFilter: boolean
    searchableColumns: string[]
    dateColumn?: string
    color?: string
    propsColumns?: string[]
}

export function DataTable({
    data,
    width,
    height,
    title,
    rowsPerPage = 10,
    enablePagination = true,
    maxRows = 100,
    enableSearch = true,
    enableDateFilter = false,
    searchableColumns = [],
    dateColumn,
    color = "#10b981",
    propsColumns
}: DataTableProps) {
    const [currentPage, setCurrentPage] = useState(1)
    const [searchTerm, setSearchTerm] = useState("")
    const [dateRange, setDateRange] = useState({ start: "", end: "" })
    const [sortColumn, setSortColumn] = useState<string>("")
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

    // Get columns from data
    const columns = useMemo(() => {
        if (propsColumns && propsColumns.length > 0) {
            return propsColumns
        }
        if (!data || data.length === 0) return []
        return Object.keys(data[0])
    }, [data])

    // Filter and search data
    const filteredData = useMemo(() => {
        let filtered = [...data]

        // Apply max rows limit
        if (maxRows > 0) {
            filtered = filtered.slice(0, maxRows)
        }

        // Apply search filter
        if (enableSearch && searchTerm) {
            filtered = filtered.filter((row) =>
                searchableColumns.some((col) =>
                    String(row[col] || "")
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                ),
            )
        }

        // Apply date filter
        if (enableDateFilter && dateColumn && (dateRange.start || dateRange.end)) {
            filtered = filtered.filter((row) => {
                const dateValue = new Date(row[dateColumn])
                const startDate = dateRange.start ? new Date(dateRange.start) : null
                const endDate = dateRange.end ? new Date(dateRange.end) : null

                if (startDate && dateValue < startDate) return false
                if (endDate && dateValue > endDate) return false
                return true
            })
        }

        // Apply sorting
        if (sortColumn) {
            filtered.sort((a, b) => {
                const aVal = a[sortColumn]
                const bVal = b[sortColumn]

                if (typeof aVal === "number" && typeof bVal === "number") {
                    return sortDirection === "asc" ? aVal - bVal : bVal - aVal
                }

                const aStr = String(aVal || "")
                const bStr = String(bVal || "")
                return sortDirection === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
            })
        }

        return filtered
    }, [
        data,
        searchTerm,
        dateRange,
        sortColumn,
        sortDirection,
        maxRows,
        enableSearch,
        enableDateFilter,
        searchableColumns,
        dateColumn,
    ])

    // Pagination
    const totalPages = enablePagination ? Math.ceil(filteredData.length / rowsPerPage) : 1
    const paginatedData = enablePagination
        ? filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
        : filteredData

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc")
        } else {
            setSortColumn(column)
            setSortDirection("asc")
        }
    }

    const formatCellValue = (value: any) => {
        if (value === null || value === undefined) return ""
        if (typeof value === "number") return value.toLocaleString()
        if (value instanceof Date) return value.toLocaleDateString()
        return String(value)
    }

    return (
        <Card className="h-full overflow-hidden" style={{ width, height }}>
            <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold" style={{ color }}>
                    {title}
                </CardTitle>

                {/* Search and Filter Controls */}
                <div className="flex flex-wrap gap-2 mt-2">
                    {enableSearch && (
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="搜尋資料..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-8"
                            />
                        </div>
                    )}

                    {enableDateFilter && dateColumn && (
                        <div className="flex gap-2">
                            <Input
                                type="date"
                                placeholder="開始日期"
                                value={dateRange.start}
                                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                                className="w-[140px]"
                            />
                            <Input
                                type="date"
                                placeholder="結束日期"
                                value={dateRange.end}
                                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                                className="w-[140px]"
                            />
                        </div>
                    )}
                </div>
            </CardHeader>

            <CardContent className="p-0 flex flex-col h-full">
                {/* Table */}
                <div className="flex-1 overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHead
                                        key={column}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => handleSort(column)}
                                    >
                                        <div className="flex items-center gap-1">
                                            {column}
                                            {sortColumn === column && <span className="text-xs">{sortDirection === "asc" ? "↑" : "↓"}</span>}
                                        </div>
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedData.map((row, index) => (
                                <TableRow key={index}>
                                    {columns.map((column) => (
                                        <TableCell key={column}>{formatCellValue(row[column])}</TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                {enablePagination && totalPages > 1 && (
                    <div className="border-t p-4">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-muted-foreground">
                                顯示 {(currentPage - 1) * rowsPerPage + 1} 到 {Math.min(currentPage * rowsPerPage, filteredData.length)}{" "}
                                筆， 共 {filteredData.length} 筆資料
                            </div>

                            <Pagination>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                        />
                                    </PaginationItem>

                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = i + 1
                                        return (
                                            <PaginationItem key={page}>
                                                <PaginationLink
                                                    onClick={() => setCurrentPage(page)}
                                                    isActive={currentPage === page}
                                                    className="cursor-pointer"
                                                >
                                                    {page}
                                                </PaginationLink>
                                            </PaginationItem>
                                        )
                                    })}

                                    <PaginationItem>
                                        <PaginationNext
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                        />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
